import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { UserContext } from '../../providers'
import {
  Box,
  Button,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Center,
} from '@chakra-ui/react'
import useFetch, { CachePolicies } from 'use-http'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { useTable } from 'react-table'
import { MainContentHeadingBox } from '../Base/MainContent'
import { PermissionsEnum } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import { checkPermission } from '../../../utils/permissions'
import moment from 'moment'
import CodeEditor from '../../components/PathPolicy/CodeEditor'
import NotFoundView from '../../components/MediaLibrary/View/NotFoundView'

const PLACEHOLDER_HANDLER = `function handler(url, urlPrefix, context) {
  return { url };
}`

const formatDate = (timestamp?: number) =>
  timestamp ? moment(timestamp).format('MMM D, YYYY [at] hh:mm a') : '—'

const UrlEndpointFunctionVersionLayout: React.FC = () => {
  const { title, version } = useParams<{ title: string; version: string }>()
  const toast = useToast()
  const { user } = useContext(UserContext)

  const { t } = useTranslation()

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [publishDetails, setPublishDetails] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchVersionDetail = useCallback(async () => {
    if (!title || !user?.clientNumber) return
    setIsLoading(true)
    try {
      const data = await request.get(
        `/api/clients/${
          user.clientNumber
        }/url-endpoint-functions/${encodeURIComponent(title)}/${version}`,
      )
      if (request.error) {
        throw data
      }
      setPublishDetails(data)
    } catch (error: any) {
      toast({
        description: error?.message || 'Unable to fetch draft details.',
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } finally {
      setIsLoading(false)
    }
  }, [title, user?.clientNumber, version])

  useEffect(() => {
    fetchVersionDetail()
  }, [fetchVersionDetail])

  const detailItems = useMemo(() => {
    const items = [
      {
        key: t('last_published_date', 'Published date'),
        value: (
          <Text color={colors.appDarkIndigo} fontWeight="semibold">
            {publishDetails?.updatedAt
              ? formatDate(publishDetails.updatedAt)
              : ''}
          </Text>
        ),
      },
      {
        key: t('description', 'Description'),
        value: (
          <Box
            display="block"
            maxH="150px"
            bg="white"
            color={colors.appDarkIndigo}
            fontWeight="semibold"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            overflowY="auto"
          >
            {publishDetails?.description}
          </Box>
        ),
      },
    ]
    return items
  }, [publishDetails])

  if (isLoading) {
    return (
      <>
        <MainContentHeadingBox></MainContentHeadingBox>
        <Center padding="16px" height="100%">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="lg"
          />
        </Center>
      </>
    )
  }
  if (!publishDetails) {
    return <NotFoundView />
  }
  return (
    <>
      <MainContentHeadingBox></MainContentHeadingBox>
      <Box padding="16px">
        <Breadcrumb
          spacing="8px"
          color={colors.appSlate}
          fontWeight="semibold"
          separator={<ChevronRightIcon color="appSlate" />}
        >
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to="/settings/url-endpoint-functions"
              _focus={{
                outline: 'none',
              }}
            >
              {t('functions', 'Functions')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to={`/settings/url-endpoint-functions/${encodeURIComponent(
                title ?? '',
              )}`}
              _focus={{
                outline: 'none',
              }}
            >
              {title}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink
              as={RouterLink}
              to={`/settings/url-endpoint-functions/${encodeURIComponent(
                title ?? '',
              )}/${version}`}
              _focus={{
                outline: 'none',
              }}
            >
              {version}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        {publishDetails && (
          <VStack align="stretch" spacing={'8px'} mt={6}>
            {detailItems.map(item => (
              <Flex key={item.key} direction="row" gap={2}>
                <Box>
                  <Text color={colors.appSlate}>{`${item.key}: `}</Text>
                </Box>
                {item.value}
              </Flex>
            ))}
          </VStack>
        )}
        <Tabs size="md" mt={8}>
          <TabList columnGap="16px">
            <Tab
              borderBottom="2px solid transparent"
              _selected={{
                color: 'appBlue',
                borderColor: 'appBlue',
                bg: '#f0f5fd',
                fontWeight: 600,
              }}
              _focus={{ outline: 'none' }}
            >
              {t('code_tab', 'Code')}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              <CodeEditor
                value={publishDetails?.code || ''}
                placeholder={PLACEHOLDER_HANDLER}
                onChange={() => {}}
                readOnly={true}
                height="300px"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  )
}

export default UrlEndpointFunctionVersionLayout
